import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  FormControl,
  OutlinedInput,
  InputLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Controller, useForm } from 'react-hook-form';

import validationUtils from '../../../services/validationUtils';
import TextInput2 from '../../form/TextInput2';
import PasswordConstraintChecker from '../../form/PasswordConstraintChecker';

const PREFIX = 'SetPasswordForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  password: `${PREFIX}-password`,
  error: `${PREFIX}-error`,
  submit: `${PREFIX}-submit`,
};

const Root = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: 305,
    padding: 32,
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(4),
  },

  [`& .${classes.title}`]: {
    fontSize: 21,
    fontFamily: 'Bitter',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.subtitle}`]: {
    fontSize: 18,
    fontWeight: 400,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.password}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.error}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.submit}`]: {
    marginTop: theme.spacing(4),
  },
}));

interface ResetPasswordFormProps {
  error?: string;
  submit: (info: any) => void;
  isProceeding?: boolean;
}

export default function DriverPasswordForm({
  error,
  submit,
  isProceeding,
}: ResetPasswordFormProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');

  const { control, formState, handleSubmit, watch, getValues } = useForm({
    defaultValues: {
      Password: '',
    } as any,
  });
  const currentPassword = watch('Password');
  const [passwordVisible, setPasswordVisible] = React.useState(false);

  return (
    <Root className={classes.container}>
      <Typography className={classes.title}>
        {t('login.resetPassword')}
      </Typography>
      <form onSubmit={handleSubmit(submit)} className={classes.form}>
        <Typography className={classes.subtitle}>
          {t('login.newPasswordRequired')}
        </Typography>
        <FormControl variant="outlined" className={classes.password}>
          <InputLabel htmlFor="password" required={true}>
            {t('login.password')}
          </InputLabel>
          <Controller
            name={'Password'}
            control={control}
            defaultValue={''}
            rules={{
              required: requiredFieldMessage,
              validate: validationUtils.validatePassword(t),
            }}
            render={({ field }) => (
              <OutlinedInput
                {...field}
                id="password"
                type={passwordVisible ? 'text' : 'password'}
                required={true}
                error={
                  formState.errors ? !!formState.errors['Password'] : false
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      onMouseDown={(evt) => evt.preventDefault()}
                      edge="end">
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t('login.password')}
              />
            )}
          />
        </FormControl>
        <PasswordConstraintChecker currentValue={currentPassword} />
        <TextInput2
          className={classes.password}
          label={t('login.confirmPassword')}
          name={'ConfirmPassword'}
          required={true}
          control={control}
          errors={formState.errors}
          rules={{
            required: requiredFieldMessage,
            validate: validationUtils.validatePasswordMatch(getValues, t),
          }}
          type="password"
        />
        <Typography color="error" className={classes.error}>
          {error}
        </Typography>
        <LoadingButton
          color="primary"
          variant="contained"
          className={classes.submit}
          loading={isProceeding}
          loadingPosition="center"
          type="submit">
          {t('login.activate')}
        </LoadingButton>
      </form>
    </Root>
  );
}
